import { ReactNode } from "react";
import { AioIconButton } from "../additional/aioIconButton";
import { AitRowGroupData } from "./functions/AitRowGroupData";
import { getBaseId } from "./functions/getBaseId";
import { ADD_ROW_GROUP, AitLocationBodyRowGroup, DUPLICATE_ROW_GROUP } from "./functions/interface";
import { trGetPositionFromLocation } from "./functions/trGetPositionFromLocation";
import { useTableContext } from "./TableContextProvider";

interface AitAddRowGroupButtonProps {
  location: AitLocationBodyRowGroup;
}

/**
 * Render the add-row-group control with menu actions for templates and duplicates.
 *
 * @template T - Table payload type
 * @param props - Component props
 * @param props.location - The row group location
 */
export const AitAddRowGroupButton = <T,>({ location }: AitAddRowGroupButtonProps): ReactNode => {
  const { state, dispatch } = useTableContext<T>();
  const currentRowGroupNumber = state?.originalTableData.bodyData.length ?? 0;
  const thisRowGroup: AitRowGroupData<T> | undefined = trGetPositionFromLocation(
    state,
    location,
    true,
  ).data;

  const id = getBaseId(location, state?.id);
  const groupTemplateNames = (
    Array.isArray(state?.groupTemplates)
      ? state.groupTemplates.filter((g) => (g.name ?? "").trim() !== "")
      : []
  )
    .map((g) => g.name!)
    .sort((a, b) => a.localeCompare(b));

  return !thisRowGroup || !state?.editable ? null : (
    <AioIconButton
      id={`${id}-add-rowgroup`}
      tipText={"Add row group"}
      iconName={"aioxPlus"}
      menuItems={[
        {
          label: "Add row group",
          action: () =>
            dispatch({ operation: ADD_ROW_GROUP, location, newLength: currentRowGroupNumber + 1 }),
        },
        {
          label: "Duplicate row group",
          action: () =>
            dispatch({
              operation: DUPLICATE_ROW_GROUP,
              location,
              newLength: currentRowGroupNumber + 1,
            }),
        },
        {
          label: "Add row group from template",
          group: groupTemplateNames.map((templateName) => ({
            label: templateName,
            action: () =>
              dispatch({
                operation: ADD_ROW_GROUP,
                location,
                templateName,
                newLength: currentRowGroupNumber + 1,
              }),
          })),
        },
      ]}
    />
  );
};

AitAddRowGroupButton.displayName = "AitAddRowGroupButton";
